(() => {
  'use strict';

  if (window.HadiyahLocalization) return;

  const config = window.__hadiyahLocalizationConfig || {};
  const activeCountry = normalizeCountry(config.activeCountry) || 'IN';
  const activeCountryName = normalizeCountryName(config.activeCountryName, activeCountry);
  const availableCountries = new Map(
    (Array.isArray(config.availableCountries) ? config.availableCountries : [])
      .map((country) => {
        const code = normalizeCountry(country?.code);
        if (!code) return null;
        return [
          code,
          {
            code,
            name: normalizeCountryName(country?.name, code),
            currency: normalizeCurrency(country?.currency) || 'INR',
          },
        ];
      })
      .filter(Boolean),
  );

  const KEYS = {
    deliveryCode: 'hadiyah:delivery-country-code',
    deliveryName: 'hadiyah:delivery-country-name',
    deliverySource: 'hadiyah:delivery-country-source',
    cleanDeliveryCountry: 'clean:delivery-country-manual:v1',
    displayCurrency: 'hadiyah_display_currency',
    displayCountry: 'hadiyah_display_country',
    cleanDisplayCurrency: 'clean:display-currency:v1',
    visitorCode: 'hadiyah:visitor-country-code',
    visitorName: 'hadiyah:visitor-country-name',
    autoAttempt: 'hadiyah:auto-localization-attempt',
    bridgeAttempt: 'hadiyah:bridge-localization-attempt',
  };

  const EURO_COUNTRIES = new Set([
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
    'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  ]);
  const COUNTRY_CURRENCIES = {
    IN: 'INR',
    PK: 'PKR',
    GB: 'GBP',
    US: 'USD',
    AE: 'AED',
    AU: 'AUD',
    SA: 'SAR',
    CA: 'CAD',
  };
  const CURRENCY_COUNTRIES = {
    INR: 'IN',
    PKR: 'PK',
    GBP: 'GB',
    USD: 'US',
    AED: 'AE',
    AUD: 'AU',
    SAR: 'SA',
    CAD: 'CA',
    EUR: 'EU',
  };
  const SUPPORTED_CURRENCIES = new Set(Object.keys(CURRENCY_COUNTRIES));

  function normalizeCountry(value) {
    const code = String(value || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) ? code : '';
  }

  function normalizeCurrency(value) {
    const code = String(value || '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(code) ? code : '';
  }

  function normalizeCountryName(value, fallback = '') {
    return String(value || fallback)
      .replace(/&amp;/gi, '&')
      .trim() || String(fallback || '').trim();
  }

  function readStorage(storage, key) {
    try {
      return storage?.getItem(key) || '';
    } catch (_error) {
      return '';
    }
  }

  function writeStorage(storage, key, value) {
    try {
      if (value) storage?.setItem(key, value);
      else storage?.removeItem(key);
    } catch (_error) {
      // Storage can be unavailable in private browsing.
    }
  }

  function countryName(code, fallback = '') {
    const normalized = normalizeCountry(code);
    return availableCountries.get(normalized)?.name || normalizeCountryName(fallback, normalized) || normalized;
  }

  function isAvailableCountry(code) {
    const normalized = normalizeCountry(code);
    return Boolean(normalized && (!availableCountries.size || availableCountries.has(normalized)));
  }

  function currencyForCountry(code) {
    const normalized = normalizeCountry(code);
    if (EURO_COUNTRIES.has(normalized)) return 'EUR';
    return COUNTRY_CURRENCIES[normalized] || 'USD';
  }

  function countryForCurrency(currency, preferredCountry = '') {
    const normalizedCurrency = normalizeCurrency(currency);
    const preferred = normalizeCountry(preferredCountry);
    if (normalizedCurrency === 'EUR' && EURO_COUNTRIES.has(preferred)) return 'EU';
    if (preferred && currencyForCountry(preferred) === normalizedCurrency) return preferred;
    return CURRENCY_COUNTRIES[normalizedCurrency] || 'IN';
  }

  const storedVisitorCountry = normalizeCountry(readStorage(window.sessionStorage, KEYS.visitorCode));
  const storedVisitorName = readStorage(window.sessionStorage, KEYS.visitorName);
  const storedLegacyDeliveryCountry = normalizeCountry(readStorage(window.localStorage, KEYS.deliveryCode));
  const storedCleanDeliveryCountry = normalizeCountry(
    readStorage(window.localStorage, KEYS.cleanDeliveryCountry),
  );
  const storedDeliveryCountry = storedLegacyDeliveryCountry || storedCleanDeliveryCountry;
  const storedDeliveryName = readStorage(window.localStorage, KEYS.deliveryName);
  let storedDeliverySource = readStorage(window.localStorage, KEYS.deliverySource);
  if (storedDeliveryCountry && storedDeliverySource !== 'auto' && storedDeliverySource !== 'manual') {
    // Existing saved destinations came from an explicit Apply action.
    storedDeliverySource = 'manual';
  }

  const storedDisplayCurrency = normalizeCurrency(readStorage(window.localStorage, KEYS.displayCurrency));
  const hasManualDisplayCurrency = SUPPORTED_CURRENCIES.has(storedDisplayCurrency);
  const initialVisitorCountry = storedVisitorCountry || activeCountry;
  const initialVisitorName = storedVisitorName || countryName(initialVisitorCountry, activeCountryName);
  const hasValidManualDelivery =
    storedDeliverySource === 'manual' && isAvailableCountry(storedDeliveryCountry);
  const initialDeliveryCountry =
    hasValidManualDelivery
      ? storedDeliveryCountry
      : initialVisitorCountry;
  const initialDeliveryName =
    hasValidManualDelivery
      ? countryName(storedDeliveryCountry, storedDeliveryName)
      : countryName(initialDeliveryCountry, initialVisitorName);

  let state = {
    visitorCountry: initialVisitorCountry,
    visitorCountryName: initialVisitorName,
    deliveryCountry: initialDeliveryCountry,
    deliveryCountryName: initialDeliveryName,
    deliverySource: hasValidManualDelivery ? 'manual' : 'auto',
    marketCountry: activeCountry,
    marketCountryName: activeCountryName,
    marketCurrency: normalizeCurrency(config.activeCurrency) || 'INR',
    displayCurrency: hasManualDisplayCurrency
      ? storedDisplayCurrency
      : currencyForCountry(initialVisitorCountry),
    displayCurrencyCountry: hasManualDisplayCurrency
      ? countryForCurrency(storedDisplayCurrency, readStorage(window.localStorage, KEYS.displayCountry))
      : countryForCurrency(currencyForCountry(initialVisitorCountry), initialVisitorCountry),
    currencyMode: hasManualDisplayCurrency ? 'manual' : 'auto',
  };

  let localizationSubmissionPending = false;
  let readyResolve;
  const ready = new Promise((resolve) => {
    readyResolve = resolve;
  });

  function snapshot() {
    return Object.freeze({ ...state });
  }

  function emit(reason) {
    document.dispatchEvent(
      new CustomEvent('hadiyah:localization:changed', {
        detail: { reason, state: snapshot() },
      }),
    );
  }

  function persistDelivery(country, name, source) {
    writeStorage(window.localStorage, KEYS.deliveryCode, country);
    writeStorage(window.localStorage, KEYS.deliveryName, name);
    writeStorage(window.localStorage, KEYS.deliverySource, source);
    writeStorage(
      window.localStorage,
      KEYS.cleanDeliveryCountry,
      source === 'manual' ? country : '',
    );
  }

  function persistDisplayCurrency(currency, country, mode) {
    if (mode === 'manual') {
      writeStorage(window.localStorage, KEYS.displayCurrency, currency);
      writeStorage(window.localStorage, KEYS.displayCountry, country);
      writeStorage(
        window.localStorage,
        KEYS.cleanDisplayCurrency,
        JSON.stringify({ version: 1, currency, country, explicit: true }),
      );
      return;
    }

    writeStorage(window.localStorage, KEYS.displayCurrency, '');
    writeStorage(window.localStorage, KEYS.displayCountry, '');
    writeStorage(window.localStorage, KEYS.cleanDisplayCurrency, '');
  }

  function setDeliveryCountry(country, name, { source = 'manual' } = {}) {
    const code = normalizeCountry(country);
    if (!isAvailableCountry(code)) return snapshot();

    const nextSource = source === 'auto' ? 'auto' : 'manual';
    const nextName = countryName(code, name);
    const changed =
      state.deliveryCountry !== code ||
      state.deliveryCountryName !== nextName ||
      state.deliverySource !== nextSource;

    state = {
      ...state,
      deliveryCountry: code,
      deliveryCountryName: nextName,
      deliverySource: nextSource,
    };
    persistDelivery(code, nextName, nextSource);
    if (changed) emit('delivery-country');
    return snapshot();
  }

  function setDisplayCurrency(currency, country = '', { mode = 'manual' } = {}) {
    const code = normalizeCurrency(currency);
    if (!SUPPORTED_CURRENCIES.has(code)) return snapshot();

    const nextMode = mode === 'auto' ? 'auto' : 'manual';
    const nextCountry = countryForCurrency(code, country || state.visitorCountry);
    const changed =
      state.displayCurrency !== code ||
      state.displayCurrencyCountry !== nextCountry ||
      state.currencyMode !== nextMode;

    state = {
      ...state,
      displayCurrency: code,
      displayCurrencyCountry: nextCountry,
      currencyMode: nextMode,
    };

    persistDisplayCurrency(code, nextCountry, nextMode);

    // Remove the previous frozen Auto Location cache. Auto now follows visitorCountry.
    writeStorage(window.localStorage, 'hadiyah_auto_location_currency', '');
    writeStorage(window.localStorage, 'hadiyah_auto_location_country', '');
    if (changed) emit('display-currency');
    return snapshot();
  }

  function useAutoDisplayCurrency() {
    const currency = currencyForCountry(state.visitorCountry);
    return setDisplayCurrency(currency, state.visitorCountry, { mode: 'auto' });
  }

  function normalizeReturnTo(value) {
    try {
      const url = new URL(value || window.location.href, window.location.origin);
      if (url.origin !== window.location.origin) return '/';
      const previewThemeId = String(config.previewThemeId || window.Shopify?.theme?.id || '');
      if (/^\d+$/.test(previewThemeId)) {
        url.searchParams.set('preview_theme_id', previewThemeId);
      }
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_error) {
      return '/';
    }
  }

  function submitMarketCountry(country, { returnTo = window.location.href } = {}) {
    const code = normalizeCountry(country);
    if (!isAvailableCountry(code) || localizationSubmissionPending) return false;
    if (code === state.marketCountry) return true;

    localizationSubmissionPending = true;
    const form = document.createElement('form');
    form.method = 'post';
    form.action = String(config.localizationUrl || '/localization');
    form.hidden = true;

    const fields = {
      form_type: 'localization',
      utf8: '✓',
      _method: 'put',
      country_code: code,
      return_to: normalizeReturnTo(returnTo),
    };
    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    HTMLFormElement.prototype.submit.call(form);
    return false;
  }

  function submitBridgedMarketCountry(country) {
    const code = normalizeCountry(country);
    if (!isAvailableCountry(code) || code === state.marketCountry) return true;
    const attempt = `${state.marketCountry}:${code}`;
    if (readStorage(window.sessionStorage, KEYS.bridgeAttempt) === attempt) return false;
    writeStorage(window.sessionStorage, KEYS.bridgeAttempt, attempt);
    return submitMarketCountry(code);
  }

  async function prepareCheckout(checkoutUrl) {
    await ready;
    if (state.deliveryCountry === state.marketCountry) return true;
    submitMarketCountry(state.deliveryCountry, { returnTo: checkoutUrl || '/checkout' });
    return false;
  }

  function detectedCountryFromPayload(payload) {
    const detected = payload?.detected_values || {};
    return normalizeCountry(
      detected.country_code || detected.country?.iso_code || detected.country?.handle,
    );
  }

  async function detectVisitorCountry() {
    if (storedVisitorCountry && isAvailableCountry(storedVisitorCountry)) {
      return {
        code: storedVisitorCountry,
        name: countryName(storedVisitorCountry, storedVisitorName),
      };
    }

    try {
      const root = String(window.Shopify?.routes?.root || config.rootUrl || '/');
      const url = new URL(`${root}browsing_context_suggestions.json`, window.location.origin);
      url.searchParams.set('country[enabled]', 'true');
      url.searchParams.set('country[exclude]', activeCountry);
      const response = await fetch(url.href, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Localization detection failed (${response.status}).`);

      const payload = await response.json();
      const code = detectedCountryFromPayload(payload);
      if (!isAvailableCountry(code)) throw new Error('Detected country is unavailable.');
      const detectedName =
        payload?.detected_values?.country?.name || countryName(code, code);
      return { code, name: countryName(code, detectedName) };
    } catch (_error) {
      return { code: activeCountry, name: activeCountryName };
    }
  }

  async function initialize() {
    const visitor = await detectVisitorCountry();
    writeStorage(window.sessionStorage, KEYS.visitorCode, visitor.code);
    writeStorage(window.sessionStorage, KEYS.visitorName, visitor.name);

    const deliveryIsManual = state.deliverySource === 'manual';
    state = {
      ...state,
      visitorCountry: visitor.code,
      visitorCountryName: visitor.name,
    };

    if (!deliveryIsManual) {
      state = {
        ...state,
        deliveryCountry: visitor.code,
        deliveryCountryName: visitor.name,
        deliverySource: 'auto',
      };
      persistDelivery(visitor.code, visitor.name, 'auto');
    }

    if (state.currencyMode === 'auto') {
      const currency = currencyForCountry(visitor.code);
      state = {
        ...state,
        displayCurrency: currency,
        displayCurrencyCountry: countryForCurrency(currency, visitor.code),
      };
      writeStorage(window.localStorage, 'hadiyah_auto_location_currency', '');
      writeStorage(window.localStorage, 'hadiyah_auto_location_country', '');
    }

    persistDelivery(state.deliveryCountry, state.deliveryCountryName, state.deliverySource);
    persistDisplayCurrency(
      state.displayCurrency,
      state.displayCurrencyCountry,
      state.currencyMode,
    );

    emit('visitor-detected');
    readyResolve(snapshot());

    if (deliveryIsManual && state.deliveryCountry !== activeCountry) {
      submitBridgedMarketCountry(state.deliveryCountry);
      return;
    }

    if (!deliveryIsManual && visitor.code !== activeCountry) {
      const attempt = `${activeCountry}:${visitor.code}`;
      if (readStorage(window.sessionStorage, KEYS.autoAttempt) !== attempt) {
        writeStorage(window.sessionStorage, KEYS.autoAttempt, attempt);
        submitMarketCountry(visitor.code);
      }
    }
  }

  window.HadiyahLocalization = Object.freeze({
    ready,
    getState: snapshot,
    currencyForCountry,
    countryForCurrency,
    setDeliveryCountry,
    setDisplayCurrency,
    useAutoDisplayCurrency,
    submitMarketCountry,
    prepareCheckout,
    isDeliveryConfirmed() {
      return state.deliverySource === 'manual';
    },
  });

  let bridgeStorageTimer;
  const reconcileBridgeStorage = () => {
    const deliveryCountry = normalizeCountry(readStorage(window.localStorage, KEYS.deliveryCode));
    const deliverySource = readStorage(window.localStorage, KEYS.deliverySource) === 'auto' ? 'auto' : 'manual';
    if (isAvailableCountry(deliveryCountry)) {
      const nextState = setDeliveryCountry(
        deliveryCountry,
        readStorage(window.localStorage, KEYS.deliveryName),
        { source: deliverySource },
      );
      if (deliverySource === 'manual' && nextState.deliveryCountry !== nextState.marketCountry) {
        submitBridgedMarketCountry(nextState.deliveryCountry);
      }
    }

    const displayCurrency = normalizeCurrency(readStorage(window.localStorage, KEYS.displayCurrency));
    if (SUPPORTED_CURRENCIES.has(displayCurrency)) {
      setDisplayCurrency(
        displayCurrency,
        readStorage(window.localStorage, KEYS.displayCountry),
        { mode: 'manual' },
      );
    } else {
      useAutoDisplayCurrency();
    }
  };

  window.addEventListener('storage', (event) => {
    if (![KEYS.deliveryCode, KEYS.deliveryName, KEYS.deliverySource, KEYS.displayCurrency, KEYS.displayCountry]
      .includes(event.key)) return;
    window.clearTimeout(bridgeStorageTimer);
    bridgeStorageTimer = window.setTimeout(reconcileBridgeStorage);
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const storedCountry = normalizeCountry(readStorage(window.localStorage, KEYS.deliveryCode));
    if (isAvailableCountry(storedCountry) && storedCountry !== state.marketCountry) {
      window.location.reload();
      return;
    }
    localizationSubmissionPending = false;
    reconcileBridgeStorage();
  });

  // Publish the initial state immediately; GeoIP reconciliation follows asynchronously.
  emit('boot');
  initialize();
})();
